/**
 * @class ConditionalVisibility
 * @extends HTMLElement
 *
 * Toggles the `open` attribute based on up to three optional selector
 * targets, all of which must be satisfied to be visible:
 *
 *   - from:    visible once this target has scrolled past (above viewport).
 *   - to:      visible while this target is out of view.
 *   - observe: visible while this target is out of view.
 */
class ConditionalVisibility extends HTMLElement {
  #fromElement = null;
  #toElement = null;
  #observeElement = null;
  #targets = new Map();
  #observer = null;

  #fromScrolledPast = false;
  #toVisible = false;
  #observeVisible = false;

  connectedCallback() {
    this.#fromElement = this.#resolveTarget('from');
    this.#toElement = this.#resolveTarget('to');
    this.#observeElement = this.#resolveTarget('observe');

    if (this.#fromElement) this.#targets.set(this.#fromElement, 'from');
    if (this.#toElement) this.#targets.set(this.#toElement, 'to');
    if (this.#observeElement) this.#targets.set(this.#observeElement, 'observe');

    if (this.#targets.size === 0) return;

    // Safe default until the observer reports actual state.
    this.#applyVisibility(false);

    this.#observer = new IntersectionObserver(this.#onIntersection, {
      root: null,
      threshold: 0,
    });

    this.#targets.forEach((_role, element) => this.#observer.observe(element));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #resolveTarget(attributeName) {
    const selector = this.getAttribute(attributeName);
    return selector ? document.querySelector(selector) : null;
  }

  #onIntersection = (entries) => {
    entries.forEach((entry) => {
      switch (this.#targets.get(entry.target)) {
        case 'from':
          this.#fromScrolledPast = entry.boundingClientRect.bottom < 0;
          break;
        case 'to':
          this.#toVisible = entry.isIntersecting;
          break;
        case 'observe':
          this.#observeVisible = entry.isIntersecting;
          break;
      }
    });

    const fromCondition = this.#fromElement ? this.#fromScrolledPast : true;
    const toCondition = this.#toElement ? !this.#toVisible : true;
    const observeCondition = this.#observeElement ? !this.#observeVisible : true;

    this.#applyVisibility(fromCondition && toCondition && observeCondition);
  };

  #applyVisibility(isVisible) {
    this.toggleAttribute('open', isVisible);
    this.toggleAttribute('inert', !isVisible);
    this.setAttribute('aria-hidden', String(!isVisible));
  }
}

if (!customElements.get('conditional-visibility')) {
  customElements.define('conditional-visibility', ConditionalVisibility);
}
